import { Calendar } from "@repo/ui";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import { addDays, format } from "date-fns";
import { useMemo, useState } from "react";

import {
  type Booking,
  useAvailability,
  useBook,
  useCancelBooking,
  useMyBookings,
} from "@/lib/queries/booking";

const HERO_IMG = "https://images.pexels.com/photos/29820786/pexels-photo-29820786.jpeg";

function toTime(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return format(d, "h:mm a");
}

export function Home() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const dateStr = useMemo(() => format(selectedDate, "yyyy-MM-dd"), [selectedDate]);
  const { data, isLoading } = useAvailability(dateStr);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [selected, setSelected] = useState<{ courtId: number; startMin: number } | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const book = useBook();

  const [manageOpen, setManageOpen] = useState(false);
  const [lookup, setLookup] = useState({ code: "", email: "" });
  const { data: my, refetch: refetchMy } = useMyBookings(lookup);
  const cancel = useCancelBooking();

  const handleBook = async () => {
    if (!selected) return;
    const res = await book.mutateAsync({
      courtId: selected.courtId,
      date: dateStr,
      startMin: selected.startMin,
      name,
      email,
      phone: phone || undefined,
    });
    alert(`Booked! Your code: ${res.code}`);
    setBookingOpen(false);
    setName("");
    setEmail("");
    setPhone("");
  };

  const disabledDays = { before: new Date(), after: addDays(new Date(), 30) };

  return (
    <div className="min-h-screen">
      <section className="relative isolate">
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt="pickleball court"
            className="h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/70 to-white" />
        </div>
        <div className="container mx-auto px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight">
              Book a Pickleball Court
            </h1>
            <p className="mt-4 text-muted-foreground text-lg">
              Reserve any of our four courts. Simple one-hour slots, same-day and up to 30 days out.
            </p>
            <div className="mt-8 grid gap-6 md:grid-cols-[320px_1fr]">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Pick a date</CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => d && setSelectedDate(d)}
                    className="rounded-md"
                    disabled={disabledDays}
                    initialFocus
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Availability</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
                  {!isLoading && data && (
                    <div className="hidden md:grid grid-cols-4 gap-4">
                      {data.courts.map((ct) => (
                        <div key={ct.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold">{ct.name}</h3>
                            <Badge variant="secondary">1 hr</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {ct.slots.map((s, i) => (
                              <Button
                                key={i}
                                variant={s.available ? "default" : "outline"}
                                disabled={!s.available || book.isPending}
                                onClick={() => {
                                  setSelected({ courtId: ct.id, startMin: s.startMin });
                                  setBookingOpen(true);
                                }}
                                className="justify-start"
                              >
                                {toTime(s.startMin)}
                              </Button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!isLoading && data && (
                    <div className="md:hidden">
                      <Tabs defaultValue={String(data.courts[0]?.id)}>
                        <TabsList className="w-full grid grid-cols-4">
                          {data.courts.map((ct) => (
                            <TabsTrigger key={ct.id} value={String(ct.id)} className="text-xs">
                              {ct.name.split(" ")[1]}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                        {data.courts.map((ct) => (
                          <TabsContent key={ct.id} value={String(ct.id)}>
                            <div className="mt-3 grid grid-cols-2 gap-2">
                              {ct.slots.map((s, i) => (
                                <Button
                                  key={i}
                                  variant={s.available ? "default" : "outline"}
                                  disabled={!s.available || book.isPending}
                                  onClick={() => {
                                    setSelected({ courtId: ct.id, startMin: s.startMin });
                                    setBookingOpen(true);
                                  }}
                                  className="justify-start"
                                >
                                  {toTime(s.startMin)}
                                </Button>
                              ))}
                            </div>
                          </TabsContent>
                        ))}
                      </Tabs>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Button variant="outline" onClick={() => setManageOpen(true)}>
                Manage booking
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter your details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1">
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="grid gap-1">
              <Label>Email</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                type="email"
              />
            </div>
            <div className="grid gap-1">
              <Label>Phone (optional)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="" />
            </div>
            <Button onClick={handleBook} disabled={!name || !email || book.isPending}>
              {book.isPending ? "Booking…" : "Confirm booking"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Find your booking</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1">
              <Label>Code</Label>
              <Input
                value={lookup.code}
                onChange={(e) => setLookup({ ...lookup, code: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="grid gap-1">
              <Label>Email</Label>
              <Input
                value={lookup.email}
                onChange={(e) => setLookup({ ...lookup, email: e.target.value })}
                type="email"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => refetchMy()}>
                Lookup
              </Button>
            </div>
            {my?.bookings?.length ? (
              <div className="space-y-2">
                {my.bookings.map((b: Booking) => (
                  <Card key={b.id}>
                    <CardContent className="py-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium">
                          Court {b.courtId} • {format(new Date(b.date + "T00:00:00"), "EEE, MMM d")}{" "}
                          • {toTime(b.startMin)}
                        </div>
                        <div className="text-xs text-muted-foreground">Status: {b.status}</div>
                      </div>
                      {b.status === "booked" && (
                        <Button
                          variant="destructive"
                          onClick={async () => {
                            await cancel.mutateAsync({ code: lookup.code, email: lookup.email });
                            refetchMy();
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Enter your code and email to view or cancel a booking.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
